import { Api } from 'src/common';
import { getUserId } from './user.service';
import { IMerchant } from 'src/common/types';
import { authenticate } from './auth-service';
import { Environment } from 'src/Environment';
import { BankName } from 'src/features/common/types/bank-details-validation';

type MerchantProfileResponse = {
  ok: boolean;
  message?: string;
  result: IMerchant;
};

export async function getMerchantProfile(
  abortController: AbortController,
): Promise<MerchantProfileResponse> {
  const userId = getUserId();
  const endpoint = `merchantprofile?userReference=${userId}`;

  const result = await Api.getV2<MerchantProfileResponse>(endpoint, {
    signal: abortController.signal,
  });

  if (result && result?.response && result.status === 200) {
    return {
      ok: true,
      result: result.response.result,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      result: result.response.result,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Get Merchant profile: Invalid response: ${result.status}: ${JSON.stringify(result.response)}`,
  );
}

export type MerchantBankDetails = {
  bankAccountHolderName: string;
  bankName: string;
  bankBranchName: string;
  bankBranchCode: string;
  bankAccountNumber: string;
  bankAccountType: number;
  mobilePhoneNumber: string;
  emailAddress: string;
  contactPerson: string;
  requestId?: string;
};

type MerchantBankDetailsResponse = {
  ok: boolean;
  message?: string;
  responseCode: number;
  errorMessage?: string;
  data: MerchantBankDetails;
};

export async function getMerchantBankDetails(
  abortController: AbortController,
  merchantId: string,
): Promise<MerchantBankDetailsResponse> {
  const accessToken = await authenticate();

  if (!merchantId || !accessToken) {
    throw new Error('Unable to get merchant bank details');
  }

  const endpoint = `/${merchantId}/merchant`;

  const result = await Api.getV2<MerchantBankDetailsResponse>(
    Environment.current.privateBaseURL + endpoint,
    {
      signal: abortController.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (result && result?.response && result.status === 200) {
    return {
      ok: true,
      data: result.response.data,
      responseCode: result.response.responseCode,
      message: result.response.errorMessage || undefined,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      data: result.response.data,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      responseCode: result.response.responseCode,
    };
  }

  throw new Error(
    `Get Merchant Bank details: Invalid response: ${result.status}: ${JSON.stringify(
      result.response,
    )}`,
  );
}

export type BankDetailsUpdate = {
  requestId: string;
  requestedDateTime: string;
  accountHolderName: string;
  bankName: BankName | string;
  branchName: string;
  branchCode: string;
  accountType: string;
  accountNumber: string;
  bankDocument: string | string[];
  mimeType: string;
  email: string;
  msisdn: string;
};

type BankDetailsUpdateResponse = {
  ok: boolean;
  message?: string;
  responseCode?: number;
  responseMessage?: string;
};

export async function updateMerchantBankDetails(
  abortController: AbortController,
  data: BankDetailsUpdate,
  merchantId: string,
): Promise<BankDetailsUpdateResponse> {
  const accessToken = await authenticate();
  const endpoint = `/merchants/${merchantId}/bankAccountUpdateRequests`;

  const result = await Api.postV2<BankDetailsUpdateResponse>(
    Environment.current.privateBaseURL + endpoint,
    {
      signal: abortController.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  );

  if (result && result?.response && (result.status === 200 || result.status === 202)) {
    return {
      ok: true,
      message: result.response.responseMessage,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      message:
        result.response?.responseMessage ??
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Update Merchant Bank details: Invalid response: ${result.status}: ${JSON.stringify(
      result.response,
    )}`,
  );
}

type ValidateBankingDetails = {
  otp: string;
};

type ValidateBankingDetailsResponse = {
  ok: boolean;
  message?: string;
  responseCode?: number;
  responseMessage?: string;
};

export async function validateUpdateBankingDetails(
  abortController: AbortController,
  data: ValidateBankingDetails,
  requestId: string,
): Promise<ValidateBankingDetailsResponse> {
  const accessToken = await authenticate();
  const endpoint = `/merchants/accountupdaterequests/${requestId}`;

  const result = await Api.patch<ValidateBankingDetailsResponse>(
    Environment.current.privateBaseURL + endpoint,
    {
      signal: abortController.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  );

  if (result && result?.response && (result.status === 200 || result.status === 202)) {
    return {
      ok: true,
      message: result.response.responseMessage,
    };
  }

  if (result.status > 300 && result.status < 500) {
    return {
      ok: false,
      message:
        result.response?.responseMessage ??
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }

  throw new Error(
    `Update Merchant Bank details: Invalid response: ${result.status}: ${JSON.stringify(
      result.response,
    )}`,
  );
}
